/**
 * Pure windowing math for VirtualList/VirtualGrid (DESIGN.md §9.2; SPEC AC-UI-1/AC-UI-2). No DOM,
 * no React — every item size is a caller-supplied constant (DESIGN.md §4.2: "Row heights are
 * constants, never measured at runtime"), so every function here is O(1) regardless of item
 * count. That is what keeps a 100,000-row list at a constant, small DOM node count instead of
 * "render everything and hope the browser copes" (SPEC 6.3's named failure mode).
 */

export interface VisibleRange {
  startIndex: number;
  /** Exclusive. */
  endIndex: number;
  /** px offset of `startIndex` from the top of the scrollable content. */
  offsetTop: number;
}

/**
 * `overscan` extra items are rendered before/after the viewport so a fast flick never shows a
 * blank edge for a frame — the free part of AC-UI-2's "no blank tiles for more than 300ms"
 * budget (the rest is the image-loading skeleton, DESIGN.md §5.3).
 */
export function computeVisibleRange(scrollOffset: number, viewportSize: number, itemCount: number, itemSize: number, overscan = 4): VisibleRange {
  if (itemCount <= 0 || itemSize <= 0 || viewportSize <= 0) {
    return { startIndex: 0, endIndex: 0, offsetTop: 0 };
  }
  const rawStart = Math.floor(scrollOffset / itemSize) - overscan;
  const startIndex = Math.max(0, rawStart);
  const visibleCount = Math.ceil(viewportSize / itemSize) + overscan * 2;
  const endIndex = Math.min(itemCount, startIndex + visibleCount);
  return { startIndex, endIndex, offsetTop: startIndex * itemSize };
}

export function totalSize(itemCount: number, itemSize: number): number {
  return Math.max(0, itemCount * itemSize);
}

/**
 * MVP-230: has the rendered window come within `threshold` items of the end of the list — i.e. is
 * it time to fetch the next page? Takes `computeVisibleRange`'s EXCLUSIVE `endIndex`, so
 * `endIndex === itemCount` means the last item is already rendered.
 *
 * `itemCount === 0` is deliberately false: an empty list has no end to reach, and firing there
 * would ask the server for "the page after nothing" every time a search returns no matches.
 */
export function isNearEnd(endIndex: number, itemCount: number, threshold: number): boolean {
  if (itemCount <= 0) return false;
  return endIndex >= itemCount - Math.max(0, threshold);
}

/**
 * Column count for a `repeat(auto-fill, minmax(minTileWidth, 1fr))` CSS grid at `containerWidth`
 * — must match that CSS formula exactly, or the virtualiser's rows will not line up with what the
 * browser actually renders. `gap` is counted once between each pair of columns, exactly like
 * CSS grid's own `gap` property.
 */
export function computeColumns(containerWidth: number, minTileWidth: number, gap: number): number {
  if (containerWidth <= 0 || minTileWidth <= 0) return 1;
  // containerWidth >= n*minTileWidth + (n-1)*gap  <=>  n <= (containerWidth + gap) / (minTileWidth + gap)
  const columns = Math.floor((containerWidth + gap) / (minTileWidth + gap));
  return Math.max(1, columns);
}

/** The actual rendered width of one column once `auto-fill`'s implicit `1fr` has grown to fill
 * `containerWidth` — needed to size the (fluid) thumbnail inside a fixed-height virtualised row. */
export function computeColumnWidth(containerWidth: number, columns: number, gap: number): number {
  if (columns <= 0) return containerWidth;
  return (containerWidth - gap * (columns - 1)) / columns;
}
